// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ReplaceLabelingTextureValidator } from './replace-labeling-texture-validator';
import { LabelStrip, LabelStripTexture, LabelStripTextureStorage } from './replace-labeling-data';
import { ReplaceLabelingDataBuilder } from './replace-labeling-data-builder';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { ProjectModel } from '../../../model/project.model';
import { ArrayUtils } from '@shared/util/array-utils';
import { ReplaceLabelingArticleCreator } from './replace-labeling-article.creator';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { AcceptingConnectionSortGroupHelper } from '@3d-scene/helper/connection/accepting-connection-sort-group.helper';
import { Connection } from '@3d-scene/model3d/connection';
import { PropertyVO } from '../../article/model/vo/property.vo';
import { CollisionHelper } from '@3d-scene/helper/collision.helper';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { LabelingTextCheckCorrectionResult, ReplaceLabelingTextCheckCorrector } from './replace-labeling-text-check-corrector';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { Vector3 } from 'three';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { Line } from '@3d-scene/model/line';
import { Injectable } from '@angular/core';
import { ArticleManager } from '@article/manager/article.manager';
import { DynamicLabelingArticleConnectProcessor } from '../../article/processor/prepare/connect/dynamic-labeling-article-connect.processor';
import { ReplaceLabelingUtils } from './replace-labeling-utils';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

class AddTextureOperation {
  public textureToAdd: LabelStripTexture;
  public addAfter: LabelStripTexture;

  constructor(textureToAdd: LabelStripTexture, addAfter: LabelStripTexture) {
    this.textureToAdd = textureToAdd;
    this.addAfter = addAfter;
  }
}

@Injectable({ providedIn: 'root' })
export class ReplaceLabelingArticleHelper {
  private _pendingLabelStrips: Map<string, LabelStrip> = new Map<string, LabelStrip>();
  private _pendingTextCheckContainers: string[] = [];

  constructor(
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private textureValidator: ReplaceLabelingTextureValidator,
    private textCheckCorrector: ReplaceLabelingTextCheckCorrector,
    private connectionTextureProcessor: LoadConnectionTextureProcessor,
    private acceptingConnectionSortHelper: AcceptingConnectionSortGroupHelper,
    private undoRedoUtils: UndoRedoUtils,
    private containerService: ContainerService,
    private articleManager: ArticleManager,
    private dynamicLabelingArticleConnectProcessor: DynamicLabelingArticleConnectProcessor,
    private replaceLabelingUtils: ReplaceLabelingUtils,
    private detachArticleUsageService: DetachArticleUsageService,
    private connectionInfoService: ConnectionInfoService
  ) {}

  public addTextCheckPendingContainers(containers: ArticleContainer[]): void {
    ArrayUtils.mergeArrays(
      this._pendingTextCheckContainers,
      containers.map((container) => container.articleGUID)
    );
  }

  public async detachLabelStrips(labelStripContainers: ArticleContainer[]): Promise<UndoDetachArticlesOperation> {
    if (ArrayUtils.isEmpty(labelStripContainers)) {
      return null;
    }

    labelStripContainers.forEach(container => this.storeLabelStrip(container));
    const detachOperation = this.undoRedoUtils.createUndoDetachOperationByArticleContainers(labelStripContainers);
    await this.detachArticleContainersFromProject(labelStripContainers);
    return detachOperation;
  }

  private async detachArticleContainersFromProject(articleContainers: ArticleContainer[]): Promise<void> {
    const usageIdsToDetach: string[] = ArticleContainerUtils.extractArticleGUIDs(articleContainers);
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(usageIdsToDetach);
  }

  private storeLabelStrip(labelStripContainer: ArticleContainer): void {
    this._pendingLabelStrips.set(
      labelStripContainer.articleGUID,
      ReplaceLabelingDataBuilder.buildLabelStrip(labelStripContainer, this.connectionInfoService, this.replaceLabelingUtils)
    );
  }

  public get pendingLabelStrips(): LabelStrip[] {
    return Array.from(this._pendingLabelStrips.values());
  }

  public async createPendingLabelingArticles(): Promise<ArticleContainer[]> {
    const labelStripsToCreate: LabelStrip[] = this.pendingLabelStrips;
    this._pendingLabelStrips.clear();

    const pendingTextCheckContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(this._pendingTextCheckContainers);
    this._pendingTextCheckContainers = [];

    labelStripsToCreate.forEach((strip) => this.updateLabelStripBackwardConnections(strip));
    const createdContainers: ArticleContainer[] = [];
    const containers = await this.createLabelingContainers(labelStripsToCreate);
    createdContainers.push(...containers);
    ArrayUtils.mergeArrays(containers, pendingTextCheckContainers);
    return this.applyTexturesToCreatedArticles(labelStripsToCreate, containers, true);
  }

  public async createLabelingArticles(labelStrips: LabelStrip[], openLabelEditor: boolean = true): Promise<ArticleContainer[]> {
    const containers = await this.createLabelingContainers(labelStrips);
    return this.applyTexturesToCreatedArticles(labelStrips, containers, openLabelEditor);
  }

  private async createLabelingContainers(labelStrips: LabelStrip[]): Promise<ArticleContainer[]> {
    if (ArrayUtils.isEmpty(labelStrips)) {
      return [];
    }

    const articleCreator: ReplaceLabelingArticleCreator = new ReplaceLabelingArticleCreator(
      this.projectModel,
      this.projectArticleService,
      this.articleManager,
      this.dynamicLabelingArticleConnectProcessor
    );
    const container = await articleCreator.createLabelingArticles(labelStrips);
    articleCreator.clear();
    return container
  }

  private updateLabelStripBackwardConnections(labelStrip: LabelStrip): void {
    const originalAcceptingConnections: Connection[] = labelStrip.getDirectAcceptingConnections();
    if (originalAcceptingConnections.length > 1) {
      const acceptingConnections: Connection[] = this.findSuitableConnectionsByCollision(originalAcceptingConnections, labelStrip.featureProperties);
      labelStrip.backwardAcceptingIds = this.filterConnectionsBySortGroupOfOriginalConnections(
        acceptingConnections,
        originalAcceptingConnections,
        labelStrip.onAdapter
      ).map((connection) => this.createIdentityFromConnection(connection));
    }
  }

  private filterConnectionsBySortGroupOfOriginalConnections(
    allConnections: Connection[],
    originalConnections: Connection[],
    onAdapter: boolean
  ): Connection[] {
    if (ArrayUtils.isEmpty(allConnections)) {
      return [];
    }

    const result: Connection[] = [];
    const sortGroups: Connection[][] = this.acceptingConnectionSortHelper.splitConnectionCubesOnGroups(allConnections, onAdapter);
    if (originalConnections.length > 0) {
      for (const sortGroup of sortGroups) {
        if (ArrayUtils.sourceArrayContainsElementFromTargetArray(originalConnections, sortGroup)) {
          result.push(...sortGroup);
        }
      }
    } else {
      const targetGroup = sortGroups[0];
      result.push(...targetGroup);
    }
    return result;
  }

  private async applyTexturesToCreatedArticles(labelStrips: LabelStrip[], createdContainers: ArticleContainer[], openLabelEditor: boolean): Promise<ArticleContainer[]> {
    if (ArrayUtils.isEmpty(createdContainers)) {
      return [];
    }

    for (const labelStrip of labelStrips) {
      this.applyTexturesToLabelStrip(
        labelStrip,
        createdContainers.filter((container) => labelStrip.newUsageIds.indexOf(container.articleGUID) >= 0)
      );
    }

    const textCheckResult: LabelingTextCheckCorrectionResult = await this.textCheckCorrector.correctTextSizeChecks(createdContainers);
    this.connectionTextureProcessor.loadAndApplyImagesToConnectionsObservable(textCheckResult.textureConnectionsToRefresh);
    createdContainers.forEach(container => container.refreshTextureConnections());
    if (openLabelEditor && textCheckResult.hasInvalidTextCheckContainers()) {
      this.textCheckCorrector.showLabelEditor(textCheckResult.invalidTextCheckContainers);
    }
    return createdContainers;
  }

  private applyTexturesToLabelStrip(sourceLabelStrip: LabelStrip, articleContainers: ArticleContainer[]): void {
    if (articleContainers.length === 0) {
      return;
    }

    const allNewTextures: LabelStripTexture[] = [];
    articleContainers.forEach((container) =>
      allNewTextures.push(...ReplaceLabelingDataBuilder.buildLabelStripTextures(container, true))
    );

    const originalTextures: LabelStripTextureStorage = new LabelStripTextureStorage(sourceLabelStrip.textures);
    const newTextures: LabelStripTextureStorage = new LabelStripTextureStorage(allNewTextures);
    this.removeNotExistingConnectionsFromOriginalTextures(originalTextures, newTextures);
    this.applyTextToNewTextures(originalTextures, newTextures);
  }

  private removeNotExistingConnectionsFromOriginalTextures(originalTextures: LabelStripTextureStorage, newTextures: LabelStripTextureStorage): void {
    let previousOriginalTexture: LabelStripTexture = null;
    let currentStripId: string = null;
    for (const originalTexture of originalTextures.textures.concat()) {
      const newTexture: LabelStripTexture = newTextures.getTextureByBackwardId(originalTexture.backwardAcceptingId);
      if (!newTexture) {
        this.textureValidator.removeTexture(originalTexture, originalTextures);
      } else {
        if (previousOriginalTexture && currentStripId !== newTexture.identity.articleUsageId) {
          this.textureValidator.splitTextureAfter(previousOriginalTexture, originalTextures);
        }
        previousOriginalTexture = originalTexture;
        currentStripId = newTexture.identity.articleUsageId;
      }
    }
  }

  private applyTextToNewTextures(originalTextures: LabelStripTextureStorage, newTextures: LabelStripTextureStorage): void {
    const texturesToAdd: AddTextureOperation[] = [];

    let previousNewTexture: LabelStripTexture = null;
    for (const newTexture of newTextures.textures) {
      const originalTexture: LabelStripTexture = originalTextures.getTextureByBackwardId(newTexture.backwardAcceptingId);
      if (originalTexture) {
        this.textureValidator.applyTextValue(originalTexture, originalTextures, newTexture, newTextures);
      } else {
        if (previousNewTexture && previousNewTexture.identity.articleUsageId === newTexture.identity.articleUsageId) {
          texturesToAdd.push(new AddTextureOperation(newTexture, previousNewTexture));
        }
      }
      previousNewTexture = newTexture;
    }
    texturesToAdd.forEach((operation) => this.textureValidator.addNewTextureAfter(operation.textureToAdd, operation.addAfter, newTextures));
  }

  public findSuitableConnectionsByCollision(acceptingConnections: Connection[], featureProperties: PropertyVO[]): Connection[] {
    const sortedConnections: Connection[] = ConnectionInfoUtils.sortConnections(acceptingConnections, false);
    const firstConnection: Connection = sortedConnections[0];
    const lastConnection: Connection = sortedConnections[sortedConnections.length - 1];

    const collisionConnections: Connection[] = CollisionHelper.findCollisionConnectionsInGlobal(
      firstConnection,
      lastConnection,
      this.containerService
    );
    const suitableConnections: Connection[] = [];
    for (const collisionConnection of collisionConnections) {
      const collisionConnectionToCheck: Connection =
        ConnectionInfoUtils.findAcceptingMultipurposeCubeByTextureCube(collisionConnection) || collisionConnection;
      if (
        !(collisionConnectionToCheck.metaElementVO instanceof ConnectionAreaVO) ||
        collisionConnectionToCheck.metaElementVO.connectionSide !== ConnectionAreaVO.SIDE_ACCEPTING
      ) {
        continue;
      }

      let isConnectionSuitable: boolean = false;
      if (sortedConnections.indexOf(collisionConnectionToCheck) < 0) {
        if (
          ConnectionInfoUtils.isFreeConnection(collisionConnectionToCheck, false) &&
          this.isConnectionSuitableByProperties(collisionConnectionToCheck, featureProperties)
        ) {
          isConnectionSuitable = true;
        }
      } else {
        isConnectionSuitable = true;
      }

      if (isConnectionSuitable) {
        suitableConnections.push(collisionConnectionToCheck);
      }
    }

    return suitableConnections;
  }

  private isConnectionSuitableByProperties(connection: Connection, featureProperties: PropertyVO[]): boolean {
    const requirementProperties: PropertyVO[] = ConnectionInfoUtils.createRequirementPropertyList(
      connection.metaElementVO as ConnectionAreaVO,
      connection.articleContainer.article
    );
    return PropertyUtils.checkProperties(featureProperties, requirementProperties);
  }

  public replaceArticleContainers(replacementContainers: ArticleContainer[], originalContainerIds: string[]): void {
    this._pendingLabelStrips.forEach((labelStrip) => this.replaceArticleContainersInStrip(replacementContainers, originalContainerIds, labelStrip));
  }

  private replaceArticleContainersInStrip(replacementContainers: ArticleContainer[], originalContainerIds: string[], labelStrip: LabelStrip): void {
    let replacementAcceptingConnections = this.findAcceptingConnectionsByLabelingData(replacementContainers, labelStrip);
    replacementAcceptingConnections = ConnectionInfoUtils.sortConnections(replacementAcceptingConnections);

    const filteredBackwardAcceptingIds: MetaElementUsageIdentityVO[] = labelStrip.backwardAcceptingIds.filter(
      (id) => originalContainerIds.indexOf(id.articleUsageId) < 0
    );
    if (filteredBackwardAcceptingIds.length !== labelStrip.backwardAcceptingIds.length) {
      labelStrip.backwardAcceptingIds = filteredBackwardAcceptingIds;
      labelStrip.backwardAcceptingIds.push(...replacementAcceptingConnections.map((connection) => this.createIdentityFromConnection(connection)));
    }
    // update backward connections to support the case when end-plate replaced by terminal
    // so additional backward-id has to be added
    this.updateLabelStripBackwardConnections(labelStrip);

    const texturesByOriginalContainers = labelStrip.textures.filter((texture) => {
      return originalContainerIds.indexOf(texture.backwardAcceptingId.articleUsageId) >= 0;
    });
    // reset direct connection-ids in case reference-id exists in order to support the case when
    // strip is connected to adapters and some terminal in the middle which is connected to label-strip
    // is replaced by terminal without suitable connection
    texturesByOriginalContainers.forEach((texture) => {
      if (texture.referenceId) {
        texture.directAcceptingId = null;
      }
    });
    if (replacementAcceptingConnections.length === texturesByOriginalContainers.length) {
      for (let i: number = 0; i < replacementAcceptingConnections.length; i++) {
        texturesByOriginalContainers[i].directAcceptingId = this.createIdentityFromConnection(replacementAcceptingConnections[i]);
      }
    }

    const baseReplacementContainerIds: string[] = ArticleContainerUtils.getBaseArticleContainers(replacementContainers).map(
      (baseContainer) => baseContainer.articleGUID
    );
    labelStrip.textures.forEach((texture) => {
      if (texture.referenceId && originalContainerIds.indexOf(texture.referenceId.articleUsageId) >= 0) {
        texture.referenceId = MetaElementUsageIdentityVO.create(baseReplacementContainerIds[0], null);
      }
    });
  }

  private findAcceptingConnectionsByLabelingData(acceptingContainers: ArticleContainer[], labelStrip: LabelStrip): Connection[] {
    const suitableConnections: Connection[] = [];
    for (const acceptingContainer of acceptingContainers) {
      const acceptingConnections: Connection[] = ConnectionInfoUtils.getFreeAcceptingConnections(acceptingContainer);
      for (const acceptingConnection of acceptingConnections) {
        if (
          this.isConnectionPlacedOnLine(acceptingConnection, labelStrip.articleLine) &&
          this.isConnectionSuitableByProperties(acceptingConnection, labelStrip.featureProperties)
        ) {
          suitableConnections.push(acceptingConnection);
        }
      }
    }

    const currentBackwardConnections: Connection[] = labelStrip.getDirectAcceptingConnections();
    const suitableGroupedConnections = this.filterConnectionsBySortGroupOfOriginalConnections(
      currentBackwardConnections.concat(suitableConnections),
      currentBackwardConnections,
      labelStrip.onAdapter
    );
    return suitableGroupedConnections.filter((connection) => currentBackwardConnections.indexOf(connection) < 0);
  }

  private isConnectionPlacedOnLine(connection: Connection, line: Line): boolean {
    if (!connection || !line) {
      return false;
    }

    const middlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(connection.compositionRectangle);
    const closestPoint: Vector3 = MathUtils.closestPointOnLine(line.start, line.end, middlePoint);
    return MathUtils.equalsNumber3D(middlePoint, closestPoint);
  }

  private createIdentityFromConnection(connection: Connection): MetaElementUsageIdentityVO {
    return MetaElementUsageIdentityVO.create(connection.articleContainer.articleGUID, connection.connectionName);
  }

  public clear(): void {
    this._pendingLabelStrips.clear();
    this._pendingTextCheckContainers = [];
  }
}
